import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { api } from '@/api/client'
import type {
  ChangePreview,
  ChangeSubscriptionBody,
  Page,
  Subscription,
} from '@/api/types'
import { i18n } from '@/i18n'
import { refreshBilling } from './billing'

export const subscriptionKeys = {
  all: ['subscriptions'] as const,
  list: (q: { status?: string; serverId?: string } = {}) => [...subscriptionKeys.all, 'list', q] as const,
  detail: (id: string) => [...subscriptionKeys.all, 'detail', id] as const,
  preview: (id: string, body: ChangeSubscriptionBody) =>
    [...subscriptionKeys.all, 'preview', id, body] as const,
}

export const subscriptionsQuery = (q: { status?: string; serverId?: string } = {}) =>
  queryOptions({
    queryKey: subscriptionKeys.list(q),
    queryFn: ({ signal }) =>
      api.get<Page<Subscription>>('/subscriptions', { query: { ...q, pageSize: 100 }, signal }),
  })

export function useSubscriptions(q: { status?: string; serverId?: string } = {}) {
  return useQuery(subscriptionsQuery(q))
}

export function useSubscription(id: string | null, opts: { refetchInterval?: number | false } = {}) {
  return useQuery({
    queryKey: subscriptionKeys.detail(id ?? ''),
    queryFn: ({ signal }) => api.get<Subscription>(`/subscriptions/${id}`, { signal }),
    enabled: Boolean(id),
    refetchInterval: opts.refetchInterval,
  })
}

/** What a plan or cycle change costs and when it applies (no side effects). */
export function useChangePreview(id: string | null, body: ChangeSubscriptionBody | null) {
  return useQuery({
    queryKey: subscriptionKeys.preview(id ?? '', body ?? { plan: 'solo', cycle: 'monthly' }),
    queryFn: ({ signal }) =>
      api.post<ChangePreview>(`/subscriptions/${id}/preview-change`, body, { signal }),
    enabled: Boolean(id && body),
    placeholderData: keepPreviousData,
    staleTime: 10_000,
    retry: false,
  })
}

/**
 * Changes plan or cycle when nothing is charged now (on the trial, not paid yet, or scheduled for the period
 * end). A change that charges now answers 409 `checkout_required`: pay it with useStartSubscriptionCheckout.
 */
export function useChangeSubscription(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ChangeSubscriptionBody) =>
      api.post<{ applied: true; subscription: Subscription }>(`/subscriptions/${id}/change`, body),
    onSuccess: () => refreshBilling(qc),
    meta: { silent: true },
  })
}

export function useDropScheduledSubscriptionChange(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<Subscription>(`/subscriptions/${id}/scheduled-change`),
    onSuccess: () => refreshBilling(qc),
    meta: { success: i18n.t('billing:subscriptions.changeDropped') },
  })
}
